import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { OccCartAdapter } from '@spartacus/cart/base/occ'
import { CART_NORMALIZER, Cart, CartModificationList } from '@spartacus/cart/base/root'
import {
  ConverterService,
  GlobalMessageType,
  LoggerService,
  OCC_CART_ID_CURRENT,
  Occ,
  OccEndpointsService,
  normalizeHttpError
} from '@spartacus/core'
import { CookieService } from 'ngx-cookie-service'
import { BehaviorSubject, Observable, of } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'
import { EbGlobalMessageService } from '../../../global-message/services/eb-global-message.service'
import {
  EbOrderEntry,
  EbOrderEntryList,
  EbQuickOrderEntry
} from '../../interface/eb-order-entry.interface'

@Injectable({ providedIn: 'root' })
export class EbCartAdapter extends OccCartAdapter {
  placeOrderError$ = new BehaviorSubject(undefined)

  protected logger = inject(LoggerService);

  constructor(
    protected http: HttpClient,
    protected occEndpointsService: OccEndpointsService,
    protected converterService: ConverterService,
    protected cookieService: CookieService,
    protected globalMessageService: EbGlobalMessageService
  ) {
    super(http, occEndpointsService, converterService)
  }

  public load(userId: string, cartId: string): Observable<Cart> {
    if (
      this.cookieService.get('access_token') ||
      this.cookieService.get('temp_customer_emulation_token')
    ) {
      if (cartId === OCC_CART_ID_CURRENT) {
        return this.loadAll(userId).pipe(
          switchMap(carts => {
            if (carts.length > 0) {
              return of(
                carts.find(cart => {
                  return cart.saveTime === undefined
                })
              )
            } else {
              // call create to make sure empty cart is created for login
              return this.create(userId).pipe(
                catchError(err => {
                  return this.showErrorMessage(normalizeHttpError(err, this.logger))
                })
              )
            }
          }),
          catchError(err => {
            return this.showErrorMessage(normalizeHttpError(err, this.logger))
          })
        )
      } else {
        return this.http
          .get<Occ.Cart>(
            this.occEndpointsService.buildUrl('cart', { urlParams: { userId, cartId } })
          )
          .pipe(
            this.converterService.pipeable(CART_NORMALIZER),
            catchError(err => {
              return this.showErrorMessage(normalizeHttpError(err, this.logger))
            })
          )
      }
    }
  }

  public addEntries(
    cartId: string,
    userId: string,
    entries: EbOrderEntry[] | EbQuickOrderEntry[]
  ): Observable<CartModificationList> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })
    let url
    if (this.isQuickAdd(entries)) {
      url = this.occEndpointsService.buildUrl('quickAddCartEntries', {
        urlParams: { userId, cartId }
      })
    } else {
      url = this.occEndpointsService.buildUrl('cartAddEntries', {
        urlParams: { userId, cartId }
      })
    }
    return this.http.post(url, { orderEntries: entries } as EbOrderEntryList, { headers })
  }

  showErrorMessage(err) {
    if(!!err?.details) {
      if (err?.details[0].type === 'EBOrderTypeError') {
        this.globalMessageService.add(
          err.details[0].message,
          GlobalMessageType.MSG_TYPE_ERROR,
          Number.MAX_VALUE
        )
      }
      if (err?.details[0].type === 'EBCartBusinessPartnerError') {
        this.globalMessageService.add(
          {key: err.details[0].message},
          GlobalMessageType.MSG_TYPE_ERROR,
          Number.MAX_VALUE
        )
      }
      if (err?.url?.includes('/orders?')) {
        this.placeOrderError$.next(true)
      }
      if (err?.details[0].type === 'EBCartInitializationError') {
        this.globalMessageService.add(
          err.details[0].message,
          GlobalMessageType.MSG_TYPE_ERROR,
          10000
        )
      }
    }
    return err
  }

  private isQuickAdd(entries: EbQuickOrderEntry[]): boolean {
    return !!entries[0].isQuickAdd
  }

  getCsvTemplate(cartId: string, userId: string) {
    return this.http.get(
      this.occEndpointsService.buildUrl('downloadCsvTemplate', {
        urlParams: { userId, cartId }
      }),
      {
        responseType: 'blob',
        headers: new HttpHeaders({
          Accept: 'text/csv'
        })
      }
    )
  }

  uploadCsvFile(cartId: string, userId: string, file) {
    return this.http.post(
      this.occEndpointsService.buildUrl('uploadCsvFile', {
        urlParams: { userId, cartId }
      }),
      file
    )
  }
}
